import { useCallback, useRef, useState } from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Badge } from "@remo-co/ui-core/src/components/Badge";
import { Provider as ThemeProvider } from "@remo-co/ui-core/src/components/Provider";
import { StyledEngineProvider } from "@remo-co/ui-core/src/components/StyledEngineProvider";
import { KeyboardArrowDown } from "@remo-co/ui-core/src/icons/KeyboardArrowDown";
import { useAppDispatch } from "store/hooks";
import Avatar from "modules/userAvatar/Avatar";
import { useI18n } from "i18n";
import {
  selectMultipleCompanyView,
  selectMyCompany,
} from "modules/company/redux/selectors";
import { selectRole, selectUser } from "modules/auth/redux/selectors";
import { showPopup } from "modules/switchCompanies/redux";
import { InEventSwitchCompanyPopup } from "modules/switchCompanies";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { getVar } from "config";
import { selectCurrentUserActivityStatus } from "modules/eventActivityStatus/redux/selectors";
import UserProfileMenuPopover from "./components/UserProfileMenuPopover";
import { useStyles } from "./styles";

export interface IProfileMenuProps {
  contain?: boolean;
  className?: string;
  border?: boolean;
  broadcast?: boolean;
}

const ProfileMenu = ({
  contain,
  border,
  broadcast,
  className,
}: IProfileMenuProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useI18n(["menu", "settings"]);
  const profileRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const company = useSelector(selectMyCompany);
  const multipleCompanyView: boolean = useSelector(selectMultipleCompanyView);
  const showMultipleCompanyMenu: boolean = !broadcast && multipleCompanyView;
  const role = useSelector(selectRole);
  const user = useSelector(selectUser);
  const eventActivityStatus = useSelector(selectCurrentUserActivityStatus);
  const classes = useStyles({
    contain,
    border,
    broadcast,
    status: eventActivityStatus?.status,
  });

  const toggleMenu = useCallback(() => {
    setShowMenu((current) => !current);
  }, []);

  const handleSwitchCompaniesPopupOpen = () => {
    dispatch(showPopup());
  };

  return (
    <>
      <Button
        className={
          showMultipleCompanyMenu
            ? classes.companyContainer
            : classes.userContainer
        }
        contentClassName={classes.containerContent}
        variant="ghost"
        color="dark"
        onClick={toggleMenu}
        ref={profileRef}
        contentWrapperClassName={classes.containerContent}
      >
        <Badge
          overlap="circular"
          variant="dot"
          classes={{ badge: classes.activityBadge }}
        >
          <div className={classNames(classes.profileContainer, className)}>
            {user && (
              <Avatar
                id="tr-avatar"
                data-automationid="user-avatar"
                user={user}
                isCompanyBadgeVisible={false}
              />
            )}
          </div>
        </Badge>
        <div
          className={classNames({
            [classes.companyInfo]: showMultipleCompanyMenu,
          })}
        >
          {showMultipleCompanyMenu && company && (
            <>
              <div>
                <Typography className={classes.companyName}>
                  {company?.name !== ""
                    ? company?.name
                    : t("menu:unnamed.company")}
                </Typography>
                <Typography className={classes.companyRole}>
                  {t(`settings:event.${role}`)}
                </Typography>
              </div>
              <KeyboardArrowDown className={classes.arrowIcon} />
            </>
          )}
        </div>
      </Button>
      <StyledEngineProvider injectFirst>
        <ThemeProvider>
          <UserProfileMenuPopover
            profileRef={profileRef}
            showMenu={showMenu}
            toggleMenu={toggleMenu}
            showSwitchCompaniesMenuOption={showMultipleCompanyMenu}
            showSwitchCompaniesPopover={handleSwitchCompaniesPopupOpen}
            user={user}
            appVersion={getVar("REACT_APP_VERSION")}
          />
        </ThemeProvider>
      </StyledEngineProvider>
      <InEventSwitchCompanyPopup />
    </>
  );
};

export default ProfileMenu;
